import { Chip, Button, Card, CardBody, Divider } from '@heroui/react';
import { DateTime } from 'luxon';
import React from 'react';
import {
  FiCalendar,
  FiUser,
  FiPhone,
  FiShoppingBag,
  FiTruck,
  FiMessageSquare,
  FiMapPin,
  FiFileText,
} from 'react-icons/fi';

import { HeaderProps, OrderCreatedContext } from './types';

const mapCreatedContextToHumanReadable = (
  createdContext: OrderCreatedContext | undefined | null
): string => {
  switch (createdContext) {
    case OrderCreatedContext.ChocoApp:
      return 'Choco App';
    case OrderCreatedContext.ChocoAi:
      return 'Choco AI';
    case OrderCreatedContext.ChocoWeb:
      return 'Choco Web';
    case OrderCreatedContext.TeamOrder:
      return 'Team Order';
    case OrderCreatedContext.WhiteLabelApp:
      return 'White Label App';
    case OrderCreatedContext.WhiteLabelWeb:
      return 'White Label Web';
    default:
      return 'Unknown';
  }
};

const formatOrderDate = (
  createdAt?: number,
  deliveryDate?: string | null
): { created: string; delivery?: string } => {
  if (!createdAt) return { created: '---' };
  const created = DateTime.fromMillis(createdAt);
  const formatted = created.toFormat('dd MMM yyyy, HH:mm');

  if (deliveryDate) {
    try {
      const delivery = DateTime.fromISO(deliveryDate);
      if (delivery.isValid) {
        return {
          created: formatted,
          delivery: delivery.toFormat('dd MMM yyyy'),
        };
      }
    } catch {
      // Invalid date, ignore
    }
  }

  return { created: formatted };
};

const getStatusColor = (
  status: string
): 'default' | 'primary' | 'success' | 'warning' | 'danger' => {
  const statusLower = status.toLowerCase();

  if (statusLower.includes('pending') || statusLower.includes('new')) {
    return 'warning';
  }
  if (
    statusLower.includes('confirmed') ||
    statusLower.includes('processing') ||
    statusLower.includes('in_progress')
  ) {
    return 'primary';
  }
  if (
    statusLower.includes('completed') ||
    statusLower.includes('delivered') ||
    statusLower.includes('success')
  ) {
    return 'success';
  }
  if (
    statusLower.includes('cancelled') ||
    statusLower.includes('rejected') ||
    statusLower.includes('failed')
  ) {
    return 'danger';
  }

  return 'default';
};

interface InfoRowProps {
  icon: React.ReactNode;
  label: string;
  value: string | null | undefined;
  'data-testid'?: string;
}

const InfoRow: React.FC<InfoRowProps> = ({
  icon,
  label,
  value,
  'data-testid': testId,
}) => {
  if (!value) return null;

  return (
    <div
      className="flex items-start gap-3 py-2"
      {...(testId && { 'data-testid': testId })}
    >
      <div className="flex-shrink-0 mt-0.5 text-default-400">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-default-500 uppercase tracking-wide mb-0.5">
          {label}
        </div>
        <div className="text-sm text-foreground break-words">{value}</div>
      </div>
    </div>
  );
};

export const Header: React.FC<HeaderProps> = ({ order }) => {
  if (!order) return null;

  const chatId = order.chat?.id;
  const statusColor = getStatusColor(order.status);
  const dateInfo = formatOrderDate(order.createdAt, order.deliveryDate);

  return (
    <div className="w-full space-y-6">
      {/* Title and Actions */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
        <div className="space-y-3">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="title-large text-foreground">
              Order {order.referenceId}
            </h2>
            <Chip
              size="md"
              variant="flat"
              color={statusColor}
              classNames={{
                base: 'border-none',
                content: 'font-semibold',
              }}
            >
              {order.status}
            </Chip>
          </div>
        </div>

        <div className="flex gap-2 flex-shrink-0">
          {chatId && (
            <Button
              as="a"
              href={`/chats/${chatId}`}
              variant="flat"
              color="primary"
              startContent={<FiMessageSquare className="h-4 w-4" />}
              data-testid="goToChatLink"
            >
              Go to Chat
            </Button>
          )}
          {order.pdf && (
            <Button
              as="a"
              href={order.pdf}
              download={`${order.user?.name ? `${order.user.name}_` : ''}${
                dateInfo.created
              }.pdf`}
              variant="flat"
              color="default"
              startContent={<FiFileText className="h-4 w-4" />}
              data-testid="downloadOrderLink"
            >
              PDF
            </Button>
          )}
        </div>
      </div>

      {/* Order Details Card */}
      <Card className="border border-default-200">
        <CardBody className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-1">
              <InfoRow
                icon={<FiCalendar className="h-4 w-4" />}
                label="Created"
                value={dateInfo.created}
              />
              {dateInfo.delivery && (
                <InfoRow
                  icon={<FiCalendar className="h-4 w-4" />}
                  label="Delivery Date"
                  value={dateInfo.delivery}
                />
              )}
              <InfoRow
                icon={<FiUser className="h-4 w-4" />}
                label="Order From"
                value={order.user?.name || null}
              />
              {order.user?.phone && (
                <InfoRow
                  icon={<FiPhone className="h-4 w-4" />}
                  label="Phone"
                  value={order.user.phone}
                />
              )}
            </div>

            {/* Right Column */}
            <div className="space-y-1">
              <InfoRow
                icon={<FiShoppingBag className="h-4 w-4" />}
                label="Buyer"
                value={order.buyer?.name || null}
              />
              <InfoRow
                icon={<FiTruck className="h-4 w-4" />}
                label="Distributor"
                value={order.supplier?.name || null}
              />
              <InfoRow
                icon={<FiMapPin className="h-4 w-4" />}
                label="Delivery Address"
                value={
                  order.chat?.deliveryAddress ||
                  order.buyer?.fullAddress ||
                  null
                }
                data-testid="DeliveryAddress"
              />
            </div>
          </div>

          {/* Additional Info */}
          {(order.body || order.createdContext) && (
            <>
              <Divider className="my-4" />
              <div className="space-y-1">
                {order.body && (
                  <InfoRow
                    icon={<FiMessageSquare className="h-4 w-4" />}
                    label="Order Comment"
                    value={order.body}
                  />
                )}
                {order.createdContext && (
                  <InfoRow
                    icon={<FiFileText className="h-4 w-4" />}
                    label="Created Via"
                    value={mapCreatedContextToHumanReadable(
                      order.createdContext
                    )}
                  />
                )}
              </div>
            </>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
